import useSWR from 'swr'
import useAuth from '@crystal-eyes/hooks/useAuth'
import { fetchV3ApiAuthed } from '@crystal-eyes/utils/apis/v3Api'
import { useCallback } from 'react'

type State = {
  data?: { id: string; name: string }[]
  error?: any
  loading: boolean
  createTag: (tag: { name: string }) => Promise<void>
  deleteTag: (tag: { id: string; name: string }) => Promise<void>
}

export default function useAllProfileTags(): State {
  const { data: auth } = useAuth()

  const fetcher = (__key: string) =>
    fetchV3ApiAuthed(auth, 'tags', { method: 'GET' }).then(async res => {
      return (await res.json()).data
    })

  const {
    data,
    isLoading,
    error,
    mutate: refetchAllTags,
  } = useSWR(auth?.authed ? 'global.user.allProfileTags' : null, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: false,
    refreshInterval: 1000 * 60 * 60,
  })

  const createTag = useCallback(
    async (tag: { name: string }) => {
      return fetchV3ApiAuthed(auth, 'tags', {
        method: 'POST',
        body: JSON.stringify({ name: tag.name }),
      }).then(() => {
        refetchAllTags()
      })
    },
    [auth, refetchAllTags],
  )

  const deleteTag = useCallback(
    async (tag: { id: string; name: string }) => {
      if (
        window.confirm(`Do you really want to delete the '${tag.name}' tag?`)
      ) {
        return fetchV3ApiAuthed(auth, `tags/${tag.id}`, {
          method: 'DELETE',
        }).then(() => {
          refetchAllTags()
        })
      } else {
        return Promise.resolve()
      }
    },
    [auth, refetchAllTags],
  )

  return {
    data,
    loading: isLoading,
    error,
    createTag,
    deleteTag,
  }
}
